import { useEffect, useRef } from "react";
import {
  skipToken,
  useMutation,
  useQuery,
  useQueryClient,
  type Mutation,
  type QueryClient,
} from "@tanstack/react-query";
import {
  deleteTotp,
  startTotpSetup,
  verifyTotpSetup,
  type DeleteTotpResponse,
  type TotpSetupResponse,
  type TotpVerifyResponse,
} from "../api/totp.api";
import {
  passkeysKeys,
  recoveryCodesAtRiskGeneration,
  sessionKeptOrEnded,
  setRecoveryCodesAtRisk,
} from "./usePasskeys";
import {
  ApiError,
  getAccessKey,
  isAccessKeyHandoff,
  isUncertainWriteFailure,
} from "@/lib/api/client";
import { useMfaRecoveryHold } from "../mfa-recovery-hold";
import { assertStepUpProofWanted, isStepUpRequired, type StepUpProof } from "../api/stepup.api";
import {
  clearPendingRecoveryCodesGeneration,
  parkRecoveryCodesGeneration,
} from "../recovery-codes-generation";

export const totpMutationKeys = {
  start: ["totp", "start"] as const,
  verify: ["totp", "verify"] as const,
  remove: ["totp", "remove"] as const,
};

export const totpActionMutationKeys = [totpMutationKeys.verify, totpMutationKeys.remove] as const;

function mutationKeyMatches(
  key: readonly unknown[] | undefined,
  expected: readonly unknown[],
): boolean {
  return (
    Array.isArray(key) &&
    key.length === expected.length &&
    key.every((part, index) => part === expected[index])
  );
}

export function isTotpActionMutationKey(key: readonly unknown[] | undefined): boolean {
  return totpActionMutationKeys.some((actionKey) => mutationKeyMatches(key, actionKey));
}

export function isProvenTotpStepUpRejection(
  mutation: Mutation<unknown, Error, unknown, unknown>,
): boolean {
  if (!isStepUpRequired(mutation.state.error)) return false;
  const vars = mutation.state.variables;
  if (vars == null || typeof vars !== "object") return false;
  return "stepUp" in vars && (vars as { stepUp?: unknown }).stepUp != null;
}

export const totpReportedStepUpRejectionIdsKey = [
  "totp",
  "reported-step-up-rejection-ids",
] as const;

function reportedStepUpRejectionIds(queryClient: QueryClient): Set<number> {
  return new Set(queryClient.getQueryData<number[]>(totpReportedStepUpRejectionIdsKey) ?? []);
}

export function hasLatestProvenTotpStepUpRejection(queryClient: QueryClient): boolean {
  const reported = reportedStepUpRejectionIds(queryClient);
  const cache = queryClient.getMutationCache();
  for (const key of totpActionMutationKeys) {
    const all = cache.findAll({ mutationKey: key });
    if (all.length === 0) continue;
    const latest = all.reduce((left, right) => (left.mutationId > right.mutationId ? left : right));
    if (isProvenTotpStepUpRejection(latest) && !reported.has(latest.mutationId)) return true;
  }
  return false;
}

export function consumeProvenTotpStepUpRejections(queryClient: QueryClient): void {
  const cache = queryClient.getMutationCache();
  const reported = reportedStepUpRejectionIds(queryClient);
  let changed = false;
  for (const key of totpActionMutationKeys) {
    for (const mutation of cache.findAll({ mutationKey: key })) {
      if (!isProvenTotpStepUpRejection(mutation)) continue;
      if (!reported.has(mutation.mutationId)) {
        reported.add(mutation.mutationId);
        changed = true;
      }
      cache.remove(mutation);
    }
  }
  if (changed) {
    queryClient.setQueryData(totpReportedStepUpRejectionIdsKey, [...reported]);
  }
}

export const pendingTotpRecoveryCodesKey = ["mfa", "pending-totp-recovery-codes"] as const;

export function parkTotpRecoveryCodes(
  queryClient: QueryClient,
  codes: string[],
  generation?: number | null,
): void {
  queryClient.setQueryDefaults(pendingTotpRecoveryCodesKey, {
    gcTime: Infinity,
    staleTime: Infinity,
  });
  queryClient.setQueryData(pendingTotpRecoveryCodesKey, codes);
  if (typeof generation === "number") {
    parkRecoveryCodesGeneration(queryClient, generation);
  }
}

export function clearPendingTotpRecoveryCodes(queryClient: QueryClient): void {
  queryClient.removeQueries({ queryKey: pendingTotpRecoveryCodesKey });
  clearPendingRecoveryCodesGeneration(queryClient);
}

export function usePendingTotpRecoveryCodes(): string[] | null {
  const { data } = useQuery<string[] | null>({
    queryKey: pendingTotpRecoveryCodesKey,
    queryFn: skipToken,
    initialData: null,
    staleTime: Infinity,
    gcTime: Infinity,
  });
  if (data === null || data === undefined || data.length === 0) return null;
  return data;
}

export function useStartTotpSetup() {
  return useMutation<TotpSetupResponse, ApiError, void>({
    mutationKey: totpMutationKeys.start,
    mutationFn: () => startTotpSetup(),
  });
}

export function useVerifyTotpSetup() {
  const queryClient = useQueryClient();
  const reportHold = useMfaRecoveryHold("totp");
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  return useMutation<
    TotpVerifyResponse,
    ApiError,
    { code: string; stepUp?: StepUpProof; replacesRecoveryCodes?: boolean },
    string | null
  >({
    mutationKey: totpMutationKeys.verify,
    gcTime: Infinity,
    onMutate: () => getAccessKey(),
    mutationFn: ({ code, stepUp }) => {
      assertStepUpProofWanted(stepUp);
      return verifyTotpSetup(code, stepUp);
    },
    onSuccess: (result, _variables, sessionKey) => {
      if (
        result.recoveryCodes &&
        result.recoveryCodes.length > 0 &&
        getAccessKey() === sessionKey
      ) {
        parkTotpRecoveryCodes(queryClient, result.recoveryCodes, result.recoveryCodesGeneration);
        if (mountedRef.current) {
          setRecoveryCodesAtRisk(queryClient, false);
          reportHold(true);
        }
      }
      queryClient.invalidateQueries({ queryKey: passkeysKeys.all });
    },
    onError: (error, variables, sessionKey) => {
      if (
        isUncertainWriteFailure(error) &&
        variables.replacesRecoveryCodes === true &&
        sessionKeptOrEnded(sessionKey)
      ) {
        setRecoveryCodesAtRisk(queryClient, true);
      }
    },
  });
}

export function useDeleteTotp() {
  const queryClient = useQueryClient();

  return useMutation<DeleteTotpResponse, ApiError, { stepUp?: StepUpProof } | void, number>({
    mutationKey: totpMutationKeys.remove,
    gcTime: Infinity,
    mutationFn: (vars) => {
      assertStepUpProofWanted(vars?.stepUp);
      return deleteTotp(vars?.stepUp);
    },
    onMutate: () => recoveryCodesAtRiskGeneration(),
    onSuccess: (result, _variables, generation) => {
      if (result.mfaEnabled === false && generation === recoveryCodesAtRiskGeneration()) {
        setRecoveryCodesAtRisk(queryClient, false, { clearDuringHandoff: true });
      }
      if (!isAccessKeyHandoff()) {
        queryClient.invalidateQueries({ queryKey: passkeysKeys.all });
      }
    },
  });
}
